import React, { FC, Fragment, useMemo } from 'react';
import {
  AddressTransactionsFilterTxType,
  BalanceResponse,
  Network,
} from '@apolloGenerated';
import { Typography } from '@rubin-dev/goblin';
import { useTranslation } from 'react-i18next';
import cls from './style.module.scss';
import { NetworkIconEnum, TableCellAmount } from '@shared/ui';
import { formatPowAmount } from '@aml/amount';
import { BalanceCard } from '@shared/ui/AddressBalanceCard/BalanceCard';
import { symbols } from '@aml/amount';

type AddressTransactionsCardProps = {
  network: string;
  isSimpleView?: boolean;
  dense?: boolean;
  balance: BalanceResponse | null;
  currentCurrency: Network | NetworkIconEnum;
};

export const AddressBalanceCard: FC<AddressTransactionsCardProps> = ({
  balance,
  dense = false,
  network,
  isSimpleView,
  currentCurrency,
}) => {
  const { t } = useTranslation();

  const node = useMemo(() => balance, [balance]);

  const total = useMemo(() => {
    if (network === Network.Btc) return node?.total;

    const findToken = node?.tokens?.find(
      (token) => token.symbol.toLowerCase() === symbols[network.toLowerCase()],
    );

    if (!findToken?.balance || !findToken?.decimals) return node?.total;

    return formatPowAmount(String(findToken?.balance), findToken?.decimals) || 0;
  }, [node, currentCurrency]);

  const totalIn = useMemo(() => {
    return node?.received && node?.received;
  }, [node, currentCurrency]);

  const totalOut = useMemo(() => {
    return node?.sent && node?.sent;
  }, [node, currentCurrency]);

  const tokensFilter = useMemo(() => {
    if (!node?.tokens?.length) return;
    const findToken = node?.tokens?.find(
      (item) => currentCurrency?.toLowerCase() === item.symbol.toLowerCase(),
    );

    if (!findToken) return;

    return {
      [findToken?.symbol]: {
        balance: findToken.balance,
        decimals: findToken.decimals,
      },
    };
  }, [balance, network, currentCurrency]);

  const renderForBTC = useMemo(
    () => (
      <div className={cls.wrapper}>
        {totalIn && (
          <div className={cls.group}>
            <Typography
              variant={dense ? 'body-14' : 'body-16'}
              color="on-surface-primary-1"
            >
              {t('strings.received')}
            </Typography>
            <Typography
              tag={'div'}
              variant={dense ? 'body-14' : 'body-16'}
              color="success-2"
              className={cls.value}
            >
              <TableCellAmount
                style={{ maxWidth: '80px' }}
                amount={totalIn}
                variant={dense ? 'body-14' : 'body-16'}
                canCopy
                type={AddressTransactionsFilterTxType.Receives}
              />
            </Typography>
          </div>
        )}
        {totalOut && (
          <div className={cls.group}>
            <Typography
              variant={dense ? 'body-14' : 'body-16'}
              color="on-surface-primary-1"
            >
              {t('strings.sent')}
            </Typography>
            <Typography
              tag={'div'}
              variant={dense ? 'body-14' : 'body-16'}
              color="error-2"
              className={cls.value}
            >
              <TableCellAmount
                style={{ maxWidth: '80px' }}
                amount={totalOut}
                variant={dense ? 'body-14' : 'body-16'}
                canCopy
                type={AddressTransactionsFilterTxType.Sent}
              />
            </Typography>
          </div>
        )}
      </div>
    ),
    [currentCurrency, tokensFilter],
  );

  const renderForTRON = useMemo(() => {
    return (
      <Fragment>
        {currentCurrency === NetworkIconEnum.Usdt && (
          <div className={cls.group}>
            <Typography
              variant={dense ? 'body-14' : 'body-16'}
              color="on-surface-primary-1"
            >
              {'USDT'}
            </Typography>
            <Typography
              tag={'div'}
              variant={dense ? 'body-14' : 'body-16'}
              color="success-2"
              className={cls.value}
            >
              <TableCellAmount
                style={{ maxWidth: '80px' }}
                amount={
                  tokensFilter?.USDT
                    ? formatPowAmount(
                        tokensFilter.USDT.balance,
                        tokensFilter.USDT.decimals,
                      )
                    : '0.00'
                }
                canCopy
                variant={dense ? 'body-14' : 'body-16'}
              />
            </Typography>
          </div>
        )}
        {currentCurrency === NetworkIconEnum.Trx && (
          <div className={cls.group}>
            <Typography
              variant={dense ? 'body-14' : 'body-16'}
              color="on-surface-primary-1"
            >
              {'TRX'}
            </Typography>
            <Typography
              tag={'div'}
              variant={dense ? 'body-14' : 'body-16'}
              color="error-2"
              className={cls.value}
            >
              <TableCellAmount
                style={{ maxWidth: '80px' }}
                amount={
                  tokensFilter?.trx
                    ? formatPowAmount(tokensFilter.trx.balance, tokensFilter.trx.decimals)
                    : '0.00'
                }
                canCopy
                variant={dense ? 'body-14' : 'body-16'}
              />
            </Typography>
          </div>
        )}
        {/*<Typography variant={dense ? 'body-14' : 'body-16'} color="on-surface-primary-2">*/}
        {/*  {t('titles.transactionsExcluded')}*/}
        {/*</Typography>*/}
      </Fragment>
    );
  }, [tokensFilter, currentCurrency]);

  const renderForETH = useMemo(() => {
    return (
      <Fragment>
        {currentCurrency === NetworkIconEnum.Usdt && (
          <div className={cls.group}>
            <Typography
              variant={dense ? 'body-14' : 'body-16'}
              color="on-surface-primary-1"
            >
              USDT
            </Typography>
            <Typography
              tag={'div'}
              variant={dense ? 'body-14' : 'body-16'}
              color="success-2"
              className={cls.value}
            >
              <TableCellAmount
                style={{ maxWidth: '80px' }}
                amount={
                  tokensFilter?.USDT
                    ? formatPowAmount(
                        tokensFilter.USDT.balance,
                        tokensFilter.USDT.decimals,
                      )
                    : '0.00'
                }
                variant={dense ? 'body-14' : 'body-16'}
                canCopy
              />
            </Typography>
          </div>
        )}
        {currentCurrency === NetworkIconEnum.Eth && (
          <div className={cls.group}>
            <Typography
              variant={dense ? 'body-14' : 'body-16'}
              color="on-surface-primary-1"
            >
              ETH
            </Typography>
            <Typography
              tag={'div'}
              variant={dense ? 'body-14' : 'body-16'}
              color="error-2"
              className={cls.value}
            >
              <TableCellAmount
                style={{ maxWidth: '80px' }}
                amount={
                  tokensFilter?.ETH
                    ? formatPowAmount(tokensFilter.ETH.balance, tokensFilter.ETH.decimals)
                    : '0.00'
                }
                canCopy
                variant={dense ? 'body-14' : 'body-16'}
              />
            </Typography>
          </div>
        )}
      </Fragment>
    );
  }, [currentCurrency, tokensFilter]);

  const renderLabel = useMemo(() => {
    switch (network) {
      case 'BTC':
        return renderForBTC;
      case 'TRON':
        return renderForTRON;
      case 'ETH':
        return renderForETH;
    }
  }, [network, balance, node, currentCurrency]);

  if (isSimpleView) {
    return (
      <div className={cls.wrapper}>
        <div className={cls.group}>
          {network === Network.Btc && (
            <>
              <Typography
                variant={dense ? 'body-14' : 'body-16'}
                color="on-surface-primary-1"
              >
                {t('strings.balance')}
              </Typography>
              <Typography
                tag={'div'}
                variant={dense ? 'body-14' : 'body-16'}
                color="on-surface-primary-1"
                className={cls.value}
              >
                <TableCellAmount
                  style={{ maxWidth: '80px' }}
                  amount={total || 0}
                  variant={dense ? 'body-14' : 'body-16'}
                  canCopy
                />
              </Typography>
            </>
          )}
        </div>
        {renderLabel}
      </div>
    );
  }

  return (
    <BalanceCard
      network={network}
      total={Number(node?.total || 0)}
      totalIn={Number(node?.sent || 0)}
      totalOut={Number(node?.received || 0)}
    />
  );
};
